import { useEffect, useState } from 'react';

/**
 * The principal returned by GET /api/auth/me (M16 T2 adds role + capabilities).
 * userId/userEmail are null for legacy (M9d) session tokens.
 */
export interface AuthPrincipal {
  tenantId: string;
  userId: number | null;
  userEmail: string | null;
  role: 'admin' | 'operator' | 'viewer';
  /**
   * True when the Worker could not read the role from the tenant DO (review
   * r6). The role/capabilities are then fail-closed placeholders — NOT the
   * user's real role — and must not be presented as a role denial.
   */
  roleLookupFailed?: boolean;
  capabilities: {
    viewDashboard: boolean;
    cardDestructive: boolean;
    syncControl: boolean;
    settings: boolean;
    managePermissions: boolean;
  };
}

export interface UseAuth {
  principal: AuthPrincipal | null;
  /** True until the first /api/auth/me round-trip settles. */
  loading: boolean;
}

/**
 * Fetch the current principal once (GET /api/auth/me). Used by the dashboard
 * layout to bounce viewers (M16 T2) and to gate nav items (hide Team/Settings
 * for non-admins). The role + capability flags come from the Worker, which
 * reads them from the tenant DO — so the SPA never re-encodes the matrix.
 *
 * On a 401 (no/invalid session) `principal` stays null; the dashboard's auth
 * guard (DashboardLayout) bounces to /login for HubSpot re-auth.
 */
export function useAuth(): UseAuth {
  const [principal, setPrincipal] = useState<AuthPrincipal | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/me')
      .then(async (res) => {
        if (!res.ok) return; // 401 → leave principal null (auth guard bounces)
        const data = (await res.json()) as AuthPrincipal;
        if (!cancelled) setPrincipal(data);
      })
      .catch(() => {
        /* non-fatal — principal stays null */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { principal, loading };
}
